import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api"; // Assuming same API service
import Select from "react-select";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";
import { customTableStyles } from "../../assets/CustomTableStyles";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullScreenLoader from "../../components/FullScreenLoader";

function SchoolVendorContractForm() {
  // --- Core State and Hooks (Kept from original pattern) ---
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const [loading, setLoading] = useState(false);

  const [isCustomDropdownValuesModalVisible, setIsCustomDropdownValuesModalVisible] = useState(false);
  const [customDropdownName, setCustomDropdownName] = useState("");

  const [vendorContractsData, setVendorContractsData] = useState([]);
  const [nextContractCode, setNextContractCode] = useState("");
  // --- State for New Dropdown Options ---
  const [contractTypeData, setContractTypeData] = useState([]);
  const [contractDurationData, setContractDurationData] = useState([]);
  const [businessTypeData, setBusinessTypeData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [bankNameData, setBankNameData] = useState([]);
  const [scopeOfServicesData, setScopeOfServicesData] = useState([]);
  const [serviceLevelData, setServiceLevelData] = useState([]);
  const [deliveryFrequencyData, setDeliveryFrequencyData] = useState([]);
  const [basisOfCalculationData, setBasisOfCalculationData] = useState([]);
  const [paymentTermData, setPaymentTermData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [complianceData, setComplianceData] = useState([]); // For Yes/No options
  const [disputeResolutionData, setDisputeResolutionData] = useState([]);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      // 1. Contract Information
      contractType: null,
      contractReferenceNo: "",
      contractTitle: "",
      effectiveDate: null,
      expiryDate: null,
      contractDuration: null,
      // 2. Vendor Information
      vendorName: "",
      businessType: null,
      companyRegistrationNo: "",
      address: "",
      city: "",
      stateProvince: "",
      zipPostalCode: "",
      country: null,
      contactPersonName: "",
      designation: "",
      mobile: "",
      email: "",
      // 3. Bank Account Details
      bankName: null,
      accountTitle: "",
      accountNumber: "",
      iban: "",
      swiftCode: "",
      // 4. Contract Scope
      scopeOfServices: null,
      serviceLevel: null,
      deliveryFrequency: null,
      // 5. Pricing & Financial Terms
      basisOfCalculation: null,
      contractValue: 0,
      paymentTerm: null,
      paymentMethod: null,
      latePaymentPenalty: "",
      discountsRebates: "",
      // 6. Compliance & Legal
      confidentialityClause: null,
      healthSafetyCompliance: null,
      insuranceCoverage: null,
      terminationClause: "",
      renewalConditions: "",
      disputeResolution: null,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
    },
  });
 const fetchNextContractCode = async () => {
    try {
      const response = await get(`SchoolVendorContract/GetNextContractCode`);
      console.log("resp",response)
      if (response.isSuccess) {
        setNextContractCode(response.code);
      } else {
        setNextContractCode("Error");
      }
    } catch (error) {
      console.error("Error fetching next contract code:", error);
      setNextContractCode("Error");
    }
  };
  // --- Reusable Function to Fetch Dropdown Data (Kept from original pattern) ---
  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(`CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response?.commonSettingDetail || []);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  // --- Fetch All Dropdown Data on Load ---
  useEffect(() => {
    fetchNextContractCode();
    fetchCommonSettings("vendorContractType", setContractTypeData);
    fetchCommonSettings("vendorContractDuration", setContractDurationData);
    fetchCommonSettings("vendorBusinessType", setBusinessTypeData);
    fetchCommonSettings("countries", setCountryData);
    fetchCommonSettings("bankNames", setBankNameData);
    fetchCommonSettings("vendorScopeOfServices", setScopeOfServicesData);
    fetchCommonSettings("vendorServiceLevel", setServiceLevelData);
    fetchCommonSettings("vendorDeliveryFrequency", setDeliveryFrequencyData);
    fetchCommonSettings("vendorBasisOfCalculation", setBasisOfCalculationData);
    fetchCommonSettings("vendorPaymentTerm", setPaymentTermData);
    fetchCommonSettings("vendorPaymentMethod", setPaymentMethodData);
    fetchCommonSettings("complianceOptions", setComplianceData);
    fetchCommonSettings("vendorDisputeResolution", setDisputeResolutionData);
  }, [token]);

  // --- CRUD Operations (Adapted for Vendor Contracts) ---
  const getVendorContractsList = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await get(`SchoolVendorContract/GetAllVendorContracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("vendor data", response.data)
      setVendorContractsData(response.data);
    } catch (error) {
      console.error("Error fetching vendor contracts:", error);
      toast.error("Failed to fetch vendor contracts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getVendorContractsList();
  }, [triggerRerender]);

  const saveData = async (data) => {
    try {
      console.log("data",JSON.stringify(data))
      const response = await post("SchoolVendorContract/UpdateVendorContract", data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.isSuccess) {
        toast.success("Vendor contract saved successfully!");
        setTriggerRerender((prev) => !prev);
        
        reset();
        fetchNextContractCode();
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error saving vendor contract:", error);
      toast.error("An error occurred while saving.");
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await get(`SchoolVendorContract/GetVendorContractById?contractId=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const vendorContract = {
          ...response.data, // Access the 'data' property
          effectiveDate: response.data.effectiveDate ? response.data.effectiveDate.split("T")[0] : null,
          expiryDate: response.data.expiryDate ? response.data.expiryDate.split("T")[0] : null,
          updatedBy: userId,
      };
      reset(vendorContract);
    } catch (error) {
      console.error("Error fetching vendor contract for editing:", error);
      toast.error("Failed to fetch contract details.");
    }
  };

  const handleDelete = async (rowId) => {
    if (window.confirm("Are you sure you want to delete this contract?")) {
      try {
        const response = await post(`SchoolVendorContract/RemoveVendorContractById?id=${rowId}&currentUserId=${userId}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.isSuccess) {
          toast.success("Contract deleted successfully!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting contract:", error);
        toast.error("Failed to delete contract.");
      }
    }
  };

  // --- DataTable Columns (Updated for Vendor Contracts) ---
  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "contractTitle",
        header: "Contract Title",
        // width: "150px",
      },
      {
        accessorKey: "vendorName",
        header: "Vendor Name",
        // width: "150px",
      },
      {
        accessorKey: "expiryDate",
        header: "Expiry Date",
        Cell: ({ cell }) => cell.getValue()?.split("T")[0],
        // width: "150px",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDelete(row.original.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = vendorContractsData.map((row, index) => ({
      "Sr No": index + 1,
      "Contract Title": row.contractTitle,
      "Vendor Name": row.vendorName,
      "Expiry Date": row.expiryDate?.split("T")[0],
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "VendorContracts");
    XLSX.writeFile(workbook, "VendorContractsList.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Vendor Contracts List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = vendorContractsData.map((row, index) => {
      return [
        index + 1,
        row.contractTitle,
        row.vendorName,
        row.expiryDate?.split("T")[0],
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("VendorContractsList.pdf");
  };

  // --- Calendar Integration (Adapted for Vendor Contracts) ---
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchEventsForCalendar = (fetchInfo, successCallback, failureCallback) => {
    get(`SchoolVendorContract/GetAllVendorContracts`)
      .then((response) => {
        if (Array.isArray(response)) {
          successCallback(response.map((contract) => ({
            id: contract.id,
            title: contract.contractTitle,
            start: contract.effectiveDate,
            end: contract.expiryDate,
            extendedProps: { vendorName: contract.vendorName },
          })));
        } else { failureCallback(new Error("Unexpected response format")); }
      })
      .catch((error) => failureCallback(error));
  };

  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    setSelectedEvent({
      contractTitle: event.title,
      vendorName: event.extendedProps.vendorName,
      startDate: event.start ? event.start.toISOString().split("T")[0] : "",
      endDate: event.end ? event.end.toISOString().split("T")[0] : "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const renderSelect = (name, label, options, moduleName, rules = { required: `${label} is required.` }) => (
    <div className="col-md-4">
      <label className="form-label">{label}<span className="text-danger fs-9">*</span></label>
      <div className="d-flex gap-2">
        <div className="w-100">
          <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => (
              <Select
                {...field}
                className="react-select-style"
                classNamePrefix="react-select-style-pref"
                options={options.map(item => ({ value: item.id, label: item.value }))}
                onChange={selectedOption => field.onChange(selectedOption ? selectedOption.value : "")}
                value={options.map(item => ({ value: item.id, label: item.value })).find(option => option.value === field.value) || null}
                isClearable
              />
            )}
          />
        </div>
        <div>
          <button type="button" className="btn btn-outline-primary" style={{ padding: "5.5px 10px" }} onClick={() => { setCustomDropdownName(moduleName); setIsCustomDropdownValuesModalVisible(true); }}>
            <i className="fas fa-plus"></i>
          </button>
        </div>
      </div>
      {errors[name] && <small className="text-danger">{errors[name].message}</small>}
    </div>
  );

  return (
    <>
      <div className="row g-3">
        {/* --- Form Section --- */}
        <div className="col-md-8">
          <form onSubmit={handleSubmit(saveData)} className="row g-3">
            {/* Section 1: Contract Information */}
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3 fs-9 border-bottom pb-2">1. Contract Information</h5>
                  <div className="row g-3">
                   <div className="col-md-4">
                      <label className="form-label">Contract Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={nextContractCode}
                        readOnly // This makes the field non-editable
                        style={{ backgroundColor: "#e9ecef" }} // Style to look disabled
                      />
                    </div>
                    {renderSelect("contractType", "Contract Type", contractTypeData, "vendorContractType")}
                    <div className="col-md-4"><label className="form-label">Contract Reference No.<span className="text-danger fs-9">*</span></label><input {...register('contractReferenceNo', { required: "This field is required." })} type="text" className="form-control" />{errors.contractReferenceNo && <small className="text-danger">{errors.contractReferenceNo.message}</small>}</div>
                    <div className="col-md-4"><label className="form-label">Contract Title / Name<span className="text-danger fs-9">*</span></label><input {...register('contractTitle', { required: "This field is required." })} type="text" className="form-control" />{errors.contractTitle && <small className="text-danger">{errors.contractTitle.message}</small>}</div>
                    <div className="col-md-4"><label className="form-label">Effective Date<span className="text-danger fs-9">*</span></label><input {...register('effectiveDate', { required: "This field is required." })} type="date" className="form-control" />{errors.effectiveDate && <small className="text-danger">{errors.effectiveDate.message}</small>}</div>
                    <div className="col-md-4"><label className="form-label">Expiry Date<span className="text-danger fs-9">*</span></label><input {...register('expiryDate', { required: "This field is required." })} type="date" className="form-control" />{errors.expiryDate && <small className="text-danger">{errors.expiryDate.message}</small>}</div>
                    {renderSelect("contractDuration", "Contract Duration", contractDurationData, "vendorContractDuration")}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Vendor Information */}
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3 fs-9 border-bottom pb-2">2. Contactor Information</h5>
                  <div className="row g-3">
                    <div className="col-md-4"><label className="form-label">Vendor / Company Name<span className="text-danger fs-9">*</span></label><input {...register('vendorName', { required: "This field is required." })} type="text" className="form-control" />{errors.vendorName && <small className="text-danger">{errors.vendorName.message}</small>}</div>
                    {renderSelect("businessType", "Business Type", businessTypeData, "vendorBusinessType")}
                    <div className="col-md-4"><label className="form-label">Company Reg. No.</label><input {...register('companyRegistrationNo')} type="text" className="form-control" /></div>
                    <div className="col-md-12"><label className="form-label">Address</label><input {...register('address')} type="text" className="form-control" /></div>
                    <div className="col-md-3"><label className="form-label">City</label><input {...register('city')} type="text" className="form-control" /></div>
                    <div className="col-md-3"><label className="form-label">State/Province</label><input {...register('stateProvince')} type="text" className="form-control" /></div>
                    <div className="col-md-3"><label className="form-label">ZIP/Postal Code</label><input {...register('zipPostalCode')} type="text" className="form-control" /></div>
                    {renderSelect("country", "Country", countryData, "countries", { required: false })}
                    <div className="col-md-4"><label className="form-label">Contact Person</label><input {...register('contactPersonName')} type="text" className="form-control" /></div>
                    <div className="col-md-4"><label className="form-label">Designation</label><input {...register('designation')} type="text" className="form-control" /></div>
                    <div className="col-md-4"><label className="form-label">Mobile</label><input {...register('mobile')} type="text" className="form-control" /></div>
                    <div className="col-md-4"><label className="form-label">Email</label><input {...register('email', { pattern: { value: /^\S+@\S+$/i, message: "Invalid email" } })} type="email" className="form-control" />{errors.email && <small className="text-danger">{errors.email.message}</small>}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Bank Account Details */}
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3 fs-9 border-bottom pb-2">3. Bank Account Details</h5>
                  <div className="row g-3">
                    {renderSelect("bankName", "Bank Name", bankNameData, "bankNames", { required: false })}
                    <div className="col-md-4"><label className="form-label">Account Title</label><input {...register('accountTitle')} type="text" className="form-control" /></div>
                    <div className="col-md-4"><label className="form-label">Account Number</label><input {...register('accountNumber')} type="text" className="form-control" /></div>
                    <div className="col-md-4"><label className="form-label">IBAN</label><input {...register('iban')} type="text" className="form-control" /></div>
                    <div className="col-md-4"><label className="form-label">Swift Code</label><input {...register('swiftCode')} type="text" className="form-control" /></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Contract Scope */}
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3 fs-9 border-bottom pb-2">4. Contract Scope</h5>
                  <div className="row g-3">
                    {renderSelect("scopeOfServices", "Scope of Services", scopeOfServicesData, "vendorScopeOfServices")}
                    {renderSelect("serviceLevel", "Service Level", serviceLevelData, "vendorServiceLevel")}
                    {renderSelect("deliveryFrequency", "Delivery Frequency", deliveryFrequencyData, "vendorDeliveryFrequency")}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Pricing & Financial Terms */}
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3 fs-9 border-bottom pb-2">5. Pricing & Financial Terms</h5>
                  <div className="row g-3">
                    {renderSelect("basisOfCalculation", "Basis of Calculation", basisOfCalculationData, "vendorBasisOfCalculation")}
                    <div className="col-md-4"><label className="form-label">Contract Value</label><input {...register('contractValue')} type="number" className="form-control" /></div>
                    {renderSelect("paymentTerm", "Payment Term", paymentTermData, "vendorPaymentTerm")}
                    {renderSelect("paymentMethod", "Payment Method", paymentMethodData, "vendorPaymentMethod")}
                    <div className="col-md-6"><label className="form-label">Late Payment Penalty</label><input {...register('latePaymentPenalty')} type="text" className="form-control" /></div>
                    <div className="col-md-6"><label className="form-label">Discounts / Rebates</label><input {...register('discountsRebates')} type="text" className="form-control" /></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Compliance & Legal */}
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3 fs-9 border-bottom pb-2">6. Compliance & Legal</h5>
                  <div className="row g-3">
                    {renderSelect("confidentialityClause", "Confidentiality Clause", complianceData, "complianceOptions")}
                    {renderSelect("healthSafetyCompliance", "Health & Safety", complianceData, "complianceOptions")}
                    {renderSelect("insuranceCoverage", "Insurance Coverage", complianceData, "complianceOptions")}
                    {renderSelect("disputeResolution", "Dispute Resolution", disputeResolutionData, "vendorDisputeResolution")}
                    <div className="col-md-6"><label className="form-label">Termination Clause</label><textarea {...register('terminationClause')} className="form-control" rows="2"></textarea></div>
                    <div className="col-md-6"><label className="form-label">Renewal Conditions</label><textarea {...register('renewalConditions')} className="form-control" rows="2"></textarea></div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Submit Button --- */}
            <div className="col-12 mt-3">
              <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Contract"}
              </button>
            </div>
          </form>
        </div>

        {/* --- Calendar Section --- */}
        <div className="col-md-4">
          <div className="calendar-container h-100 card">
            <div className="card-body">
              <FullCalendar plugins={[dayGridPlugin, interactionPlugin]} initialView="dayGridMonth" events={fetchEventsForCalendar} eventClick={handleEventClick} dayMaxEvents={1} height="auto" headerToolbar={{ left: "prev,next", center: "title", right: "dayGridMonth" }} />
            </div>
          </div>
        </div>
      </div>

      {/* --- Data Table Section --- */}
      <div className="row mt-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="mb-3 fs-8">Vendor Contracts List</h4>
              <CustomDatatableGrid
                columns={columns}
                data={vendorContractsData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                    columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* --- Modals --- */}
      {isCustomDropdownValuesModalVisible && (<DropdownWithCustomValuesModal dropdownName={customDropdownName} 
       onClose={() => {
        // First, close the modal
        setIsCustomDropdownValuesModalVisible(false);

        // --- THIS IS THE NEW LOGIC ---
        // Now, check which dropdown was updated and re-fetch its data.
        // This updates the state and forces React to re-render the dropdown.
        switch (customDropdownName) {
          case "vendorContractType":
            fetchCommonSettings("vendorContractType", setContractTypeData);
            break;
          case "vendorContractDuration":
            fetchCommonSettings("vendorContractDuration", setContractDurationData);
            break;
          case "vendorBusinessType":
            fetchCommonSettings("vendorBusinessType", setBusinessTypeData);
            break;
          case "countries":
            fetchCommonSettings("countries", setCountryData);
            break;
          case "bankNames":
            fetchCommonSettings("bankNames", setBankNameData);
            break;
          case "vendorScopeOfServices":
            fetchCommonSettings("vendorScopeOfServices", setScopeOfServicesData);
            break;
          case "vendorServiceLevel":
            fetchCommonSettings("vendorServiceLevel", setServiceLevelData);
            break;
          case "vendorDeliveryFrequency":
            fetchCommonSettings("vendorDeliveryFrequency", setDeliveryFrequencyData);
            break;
          case "vendorBasisOfCalculation":
            fetchCommonSettings("vendorBasisOfCalculation", setBasisOfCalculationData);
            break;
          case "vendorPaymentTerm":
            fetchCommonSettings("vendorPaymentTerm", setPaymentTermData);
            break;
          case "vendorPaymentMethod":
            fetchCommonSettings("vendorPaymentMethod", setPaymentMethodData);
            break;
          case "complianceOptions":
            fetchCommonSettings("complianceOptions", setComplianceData);
            break;
          case "vendorDisputeResolution":
            fetchCommonSettings("vendorDisputeResolution", setDisputeResolutionData);
            break;
          default:
            break;
        }}}
      
      />)}
      {showModal && selectedEvent && (
        <>
          <div className="modal fade show" style={{ display: "block" }}><div className="modal-dialog"><div className="modal-content"><div className="modal-header"><h5 className="mb-0">Contract Details</h5><button type="button" className="btn-close" onClick={handleCloseModal}></button></div><div className="modal-body"><p><strong>Title:</strong> {selectedEvent.contractTitle}</p><p><strong>Vendor:</strong> {selectedEvent.vendorName}</p><p><strong>Start:</strong> {selectedEvent.startDate}</p><p><strong>End:</strong> {selectedEvent.endDate}</p></div><div className="modal-footer"><button type="button" className="btn btn-primary" onClick={handleCloseModal}>Close</button></div></div></div></div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {loading && <FullScreenLoader />}
    </>
  );
}

export default SchoolVendorContractForm;